<template>
  <div>
    <input
      type="text"
      ref="searchInput"
      placeholder="Ctrl/Cmd+K"
      class="placeholder:text-fg-placeholder focus:outline-none focus:border-heading focus:ring-heading focus:ring-2 block bg-surface-primary w-48 border border-solid border-linear-subtle rounded text-sm px-2 py-1"
      v-model.trim="searchQuery"
      @input="runSearch"
    />
    <div
      ref="floatingMenu"
      :style="floatingStyles"
      v-show="isMenuVisible"
      class="bg-surface-primary rounded border border-linear-subtle p-8 z-20 flex flex-col items-start justify-start gap-4 xl:flex-row overflow-y-auto tt-scroll max-h-[70vh] space-y-6 xl:space-y-0"
    >
      <div class="space-y-2 w-52">
        <LabelDefault>Guests & Reservations</LabelDefault>
        <ul v-for="guest in searchResults.guests" :key="guest.id" class="space-y-1">
          <li class="guest-name text-fg-link-primary cursor-pointer hover:opacity-80">
            <router-link
              :id="`guest-${guest.id}`"
              :to="{ name: 'guest', params: { id: guest.id } }"
            >
              {{ guest.first_name }} {{ guest.last_name }}
            </router-link>
          </li>
          <li
            v-for="reservation in guest.recent_reservations"
            :key="reservation.id"
            class="pl-4 text-fg-link-primary cursor-pointer hover:opacity-80"
          >
            <router-link
              :id="`reservation-${reservation.id}`"
              :to="{
                name: PERFORMANCE_RESERVATION_ROUTES.RESERVATION_DETAIL,
                params: {
                  performanceId: `p_${reservation.performance_id}`,
                  reservationId: `pr_${reservation.id}`,
                },
              }"
            >
              {{ settingStore.longDate(reservation.performance.datetime) }}
            </router-link>
          </li>
        </ul>
      </div>
      <div class="space-y-2 w-52">
        <LabelDefault>Shows & Performances</LabelDefault>
        <ul v-for="show in searchResults.shows" :key="show.id" class="space-y-1">
          <li class="show-name text-fg-link-primary cursor-pointer hover:opacity-80">
            <router-link
              :id="`show-${show.id}`"
              :to="{ name: SHOW_ROUTE_NAMES.DETAIL, params: { showId: show.id } }"
            >
              {{ show.name }}
            </router-link>
          </li>
          <li
            v-for="performance in show.upcoming_performances"
            :key="performance.id"
            class="pl-4 text-fg-link-primary cursor-pointer hover:opacity-80"
          >
            <router-link
              :id="`performance-${performance.id}`"
              :to="{
                name: PERFORMANCE_ROUTE_NAMES.SCHEDULE,
                params: { performanceId: `p_${performance.id}` },
              }"
            >
              {{ settingStore.longDate(performance.datetime) }}
            </router-link>
          </li>
        </ul>
      </div>
      <div class="space-y-2 w-52">
        <LabelDefault>Residencies</LabelDefault>
        <ul
          class="text-fg-link-primary cursor-pointer hover:opacity-80"
          v-for="residency in searchResults.residencies"
          :key="residency.id"
        >
          <li class="residency-name text-fg-link-primary cursor-pointer hover:opacity-80">
            <router-link
              :id="`residency-${residency.id}`"
              :to="{ name: RESIDENCY_ROUTE_NAMES.DETAIL, params: { residencyId: residency.id } }"
            >
              {{ residency.name }}
            </router-link>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Placement, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue'
import { onClickOutside, useDebounceFn, useMagicKeys } from '@vueuse/core'
import { computed, ref, useTemplateRef } from 'vue'

import LabelDefault from '@/common/components/atoms/LabelDefault.vue'
import {
  PERFORMANCE_RESERVATION_ROUTES,
  PERFORMANCE_ROUTE_NAMES,
} from '@/modules/performance/constants/PerformanceRouteConstants'
import { RESIDENCY_ROUTE_NAMES } from '@/modules/residency/constants/ResidencyRouteNames'
import { SHOW_ROUTE_NAMES } from '@/modules/show/constants/ShowRouteName'
import VenueService, { SearchResponse } from '@/modules/venueUi/services/VenueService'
import { useSettingStore } from '@/stores/setting'

const props = withDefaults(defineProps<{ placement?: Placement }>(), { placement: 'bottom' })
const settingStore = useSettingStore()

const searchInput = useTemplateRef<HTMLInputElement>('searchInput')
const floatingMenu = useTemplateRef<HTMLDivElement>('floatingMenu')
const isMenuVisible = ref<boolean>(false)
onClickOutside(floatingMenu, clearSearch, { ignore: [searchInput] })
const { floatingStyles } = useFloating(searchInput, floatingMenu, {
  placement: props.placement,
  middleware: [offset(10), flip({ fallbackAxisSideDirection: 'start' }), shift({ padding: 10 })],
  whileElementsMounted: autoUpdate,
})

const createEmptyResults = (): SearchResponse => ({ guests: [], shows: [], residencies: [] })
const searchQuery = ref<string>('')
const searchResults = ref<SearchResponse>(createEmptyResults())
const hasSearchResults = computed<boolean>(() => {
  return (
    searchResults.value.guests.length > 0 ||
    searchResults.value.shows.length > 0 ||
    searchResults.value.residencies.length > 0
  )
})
const runSearch = useDebounceFn(performSearch, 500)
async function performSearch(): Promise<void> {
  if (!searchQuery.value || searchQuery.value.length < 2) {
    searchResults.value = createEmptyResults()
    isMenuVisible.value = false
    return
  }
  try {
    const { data } = await VenueService.search(searchQuery.value)
    searchResults.value = data
    isMenuVisible.value = hasSearchResults.value
  } catch {
    isMenuVisible.value = false
  }
}

function clearSearch(): void {
  searchQuery.value = ''
  searchResults.value = createEmptyResults()
  isMenuVisible.value = false
}
useMagicKeys({ passive: false, onEventFired: handleShortcutEvent })
function handleShortcutEvent(e: KeyboardEvent): void {
  if (e.type !== 'keydown') return
  if (e.key === 'Escape') {
    clearSearch()
    return
  }
  const isK: boolean = e.key?.toLowerCase() === 'k'
  const isSearchShortcut: boolean = isK && (e.ctrlKey || e.metaKey)
  if (!isSearchShortcut) return
  e.preventDefault()
  e.stopPropagation()
  searchInput.value?.focus()
}
</script>
